import { AsksStatus } from '@/types/skuDataBackend';
import {
  eachDayOfInterval,
  endOfDay,
  startOfYear,
  startOfDay,
  addDays,
} from 'date-fns';
import _sortBy from 'lodash/sortBy';
import { getLatestStatus } from './get-latest-status';
import { SkuOrderStatusStatus } from '@/types/OrderStatusType';
import {
  AskMarketValue,
  BidMarketValue,
  OrderMarketValue,
} from '@/types/utilities';
import checkArray from './check-array';
import { defaultBackendDate } from '@/constants/others';
export const checkCurrentStatusIcon = (
  status: string,
  skuOrderStatus: SkuOrderStatusStatus
) => {
  let lastStatus = 'matched';
  const currentStatus = getLatestStatus(skuOrderStatus);
  if (currentStatus == 'matched') lastStatus = 'authentication';
  if (
    currentStatus == 'authenticated' ||
    currentStatus == 'authenticationCancelled'
  )
    lastStatus = 'payment';
  if (currentStatus == 'paid') lastStatus = 'shipping';
  if (
    currentStatus == 'shippingLabelProvided' ||
    currentStatus == 'shippedBySeller' ||
    currentStatus == 'shippedByCarrier'
  )
    lastStatus = 'shipping';
  if (
    currentStatus == 'recievedByBuyer' ||
    currentStatus == 'recievedByTimeout' ||
    currentStatus == 'recievedByCarrier'
  )
    lastStatus = 'payout';
  if (
    currentStatus == 'triggerPayout' ||
    currentStatus == 'payoutCreated' ||
    currentStatus == 'payoutFailed' ||
    currentStatus == 'payoutDone'
  )
    lastStatus = 'payout';

  return status == lastStatus;
};

export const getMarketValue = (
  asks: AskMarketValue[] = [],
  bids: BidMarketValue[] = [],
  orders: OrderMarketValue[] = [],
  retailPrice?: number,
  { before, after }: { before?: Date; after?: Date } = {}
) => {
  // lowest asks
  const openAsks = asks
    .filter((el) => {
      const beforeOk = before ? new Date(el.created) <= before : true;
      const afterOk = after ? new Date(el.created) >= after : true;
      return beforeOk && afterOk;
    })
    .sort((a, b) => a.value - b.value);

  // highest bids
  const openBids = bids
    .filter((el) => {
      const beforeOk = before ? new Date(el.created) <= before : true;
      const afterOk = after ? new Date(el.created) >= after : true;
      return beforeOk && afterOk;
    })
    .sort((a, b) => a.value - b.value)
    .reverse();

  // hghest sales
  const allOrders = [...orders];

  const sales = allOrders
    .filter((el) => {
      const beforeOk = before
        ? new Date(el.skuOrderStatus?.paid!) <= before
        : true;
      const afterOk = after
        ? new Date(el.skuOrderStatus?.paid!) >= after
        : true;
      return beforeOk && afterOk;
    })
    .sort((a, b) => a.payValue - b.payValue)
    .reverse();

  const maxAsks = 2;
  const maxBids = 2;
  const maxSales = 2;

  const askSlices = openAsks.slice(0, maxAsks).map((el) => el.value);
  const askQuot =
    askSlices.reduce((a: number, b: number) => a + b, 0) / askSlices.length;

  //console.log('askQuot ', askQuot)

  const bidSlices = openBids.slice(0, maxBids).map((el) => el.value);
  const bidQuot =
    bidSlices.reduce((a: number, b: number) => a + b, 0) / bidSlices.length;

  //console.log('bidQuot ', bidQuot)

  const salesSlices = sales.slice(0, maxSales).map((el) => el.payValue);

  const salesQuot =
    salesSlices.reduce((a: number, b: number) => a + b, 0) / salesSlices.length;

  //console.log('salesQuot ', salesQuot)

  //console.log([askQuot, bidQuot, salesQuot, salesQuot])

  const a = [askQuot, bidQuot, salesQuot, salesQuot].filter((el) => !!el);
  //console.log('created array ', a)
  const value = a.reduce((a: number, b: number) => a + b, 0) / a.length;

  //console.log('value ', value)

  // ceil 13450 to 13500, displayed value will be 135. This creates some "unschärfe" if multiplied in
  // the displayed values, but they are just "richtwerte" and do not need to be exact
  const c = Math.ceil(value / 100) * 100;

  //console.log('Math.ceil value ', c)

  return {
    value: c || -1 || 0,
    askSlices,
    askQuot,
    bidSlices,
    bidQuot,
    salesSlices,
    salesQuot,
    finalDiv: a.length,
  };
};

export const genMarketValueChartDataForSku = (
  asks: AskMarketValue[],
  bids: BidMarketValue[],
  orders: OrderMarketValue[],
  retailPrice: number
) => {
  const _data: any = [];
  // aggregate timestamps by day when changes which impact the market value happen
  const pits: number[] = [];
  //console.log(sku)
  if (checkArray(asks)) {
    for (const ask of asks) {
      pits.push(new Date(ask.created).getTime());
      if (ask.status == AsksStatus.FULFILLED)
        pits.push(new Date(ask.fulfilledDate).getTime());
      if (ask.status == AsksStatus.FULFILLED_EXTERNAL)
        pits.push(new Date(ask.fulfilledDate).getTime());
    }
  }
  if (checkArray(bids)) {
    for (const bid of bids) {
      pits.push(new Date(bid.created).getTime());
    }
  }
  if (checkArray(orders)) {
    for (const order of orders) {
      if (order?.skuOrderStatus?.paid) {
        pits.push(new Date(order.skuOrderStatus.paid).getTime());
      }
    }
  }

  try {
    const startDate = getStartDate(asks, bids, orders);
    const start = startDate
      ? getStartDate(asks, bids, orders)
      : startOfYear(new Date());
    const daysOfInterval = eachDayOfInterval({ start, end: new Date() });

    let marketValue = { value: 0 };

    for (let i = 0; i < daysOfInterval.length; i++) {
      const day = daysOfInterval[`${i}`];
      marketValue = getMarketValue(asks, bids, orders, retailPrice, {
        before: endOfDay(day),
        after: startOfDay(day),
      });

      _data.push({
        value: marketValue.value,
        created: day,
      });
    }

    // Add current data point
    _data.push({
      value: marketValue.value,
      created: new Date(),
    });
  } catch (e) {}

  return {
    marketValueChartData: _data,
    /** if you just need these, use getMarketValue() */
    marketValue: _data.length ? _data[_data.length - 1].value : -1,
    marketValueCreated: _data.length
      ? _data[_data.length - 1].created
      : new Date(defaultBackendDate),
  };
};

export const getMarketValueForToday = (
  asks: AskMarketValue[],
  bids: BidMarketValue[],
  orders: OrderMarketValue[],
  retailPrice: number,
  dayToGet?: Date,
  dayBefore?: Date
): number => {
  const day = dayToGet ? dayToGet : new Date();
  const startDate = getStartDate(asks, bids, orders);
  const daysBefore = dayBefore ? dayBefore : addDays(day, -1);
  const marketValue = getMarketValue(asks, bids, orders, undefined, {
    before: endOfDay(day),
    after: startOfDay(daysBefore),
  });
  if (dayBefore && dayBefore < startDate) {
    return retailPrice || 0;
  }
  if (marketValue.value === 0 || marketValue.value === -1) {
    return getMarketValueForToday(
      asks,
      bids,
      orders,
      retailPrice,
      day,
      addDays(daysBefore, -1)
    );
  }
  return isNaN(marketValue.value) ? 0 : marketValue.value;
};

export const getStartDate = (
  asks: AskMarketValue[],
  bids: BidMarketValue[],
  orders: OrderMarketValue[]
) => {
  let smallestDate = new Date();
  if (checkArray(orders)) {
    for (const order of orders) {
      if (order?.skuOrderStatus?.paid) {
        const orderCreatedDate = new Date(order.skuOrderStatus.paid);
        if (orderCreatedDate < smallestDate) {
          smallestDate = orderCreatedDate;
        }
      }
    }
  }
  if (checkArray(asks)) {
    for (const ask of asks) {
      const askCreatedDate = new Date(ask.created);
      if (askCreatedDate < smallestDate) {
        smallestDate = askCreatedDate;
      }
    }
  }
  if (checkArray(bids)) {
    for (const bid of bids) {
      const bidCreatedDate = new Date(bid.created);
      if (bidCreatedDate < smallestDate) {
        smallestDate = bidCreatedDate;
      }
    }
  }
  return addDays(smallestDate, -1);
};
